import {
  ApiIcon,
  BankIcon,
  BuildingIcon,
  DocumentIcon,
  DollarIcon,
  FileSettingsIcon,
  GridSquaresIcon,
  HomeIcon,
  AlertIcon,
  PhoneIcon,
  PlusIcon,
  SearchIcon,
  SecureMessageIcon,
  TreeDiagramIcon,
  TrendUpIcon,
  UserIcon,
  WalletIcon,
} from '@sharedCR/coreDesignSystem/icons';
import { PATH_PROFILE } from 'app/configs/routesConfig/profilePage';

import { NAVIGATION_ITEM_TYPE } from 'constants/navigation';
import { COLOR_PALETTE } from 'constants/ui';

/** Indigo accent for the active nav tab (matches the "Quiet Instrument" mobile-nav design). */
export const NAV_ACCENT_COLOR = COLOR_PALETTE.PURPLE[7];

/** Indigo→violet gradient filling the active tab's pill. */
export const NAV_ACTIVE_GRADIENT = `linear-gradient(135deg, ${COLOR_PALETTE.PURPLE[6]} 0%, ${COLOR_PALETTE.PURPLE[8]} 100%)`;

/** Icon color inside the filled active pill (white, reads on the gradient). */
export const NAV_ACTIVE_ICON_COLOR = COLOR_PALETTE.GRAY[1];

export const MORE_ITEM_ID = 'more';

export const SEARCH_ITEM_ID = 'search';

export const PROFILE_ITEM_ID = 'profile';

export const NOTIFICATIONS_ITEM_ID = 'notifications';

export const MESSAGES_ITEM_ID = 'messages';

export const PHONE_ITEM_ID = 'phone';

export const CREATE_ITEM_ID = 'create';

/**
 * Short, single-word labels for the bottom-nav tabs (the full `navigationConfig` titles
 * wrap to two lines on a phone and misalign the icon row). Falls back to the item's title.
 */
export const BOTTOM_NAV_LABELS = {
  home: 'Home',
  pipelines: 'Pipeline',
  dashboards: 'Dashboards',
  reports: 'Reports',
  documentData: 'Documents',
  pricingWorkbench: 'Pricing',
  'company-center': 'Company',
  'company-manager': 'Company',
  'applications-tab': 'Settings',
  'accounting-tab': 'Accounting',
  'system-tab': 'System',
  profile: 'Profile',
  [NOTIFICATIONS_ITEM_ID]: 'Notifications',
  [MESSAGES_ITEM_ID]: 'Messages',
  [PHONE_ITEM_ID]: 'Phone',
  [CREATE_ITEM_ID]: 'Create',
};

// Note: The primary-tabs allowlist is kept locally in computeVisibleTabs (BottomNav.utils.js)
// to avoid an exported constant with no external consumers.

/** Synthetic "My Profile" tab — not a `navigationConfig` entry; always available when logged in. */
export const PROFILE_NAV_ITEM = {
  id: PROFILE_ITEM_ID,
  title: 'Profile',
  type: NAVIGATION_ITEM_TYPE.ITEM,
  url: PATH_PROFILE,
};

/** Icon glyph size (px) used in the bottom-nav tabs and the More sheet rows. */
export const BOTTOM_NAV_ICON_SIZE = 24;

/** Opacity of the light glass bar surface — low so content is clearly visible through the blur. */
export const GLASS_BG_ALPHA = 0.16;

/** Opacity of the subtle rim-light highlight along the bar's top edge (frosted-glass sheen). */
export const NAV_GLASS_RIM_ALPHA = 0.16;

/** Backdrop-filter applied to the glass surfaces. */
export const NAV_GLASS_BLUR = 'saturate(200%) blur(30px)';

/** Opacity of the faint indigo tint washed across the frosted glass. */
export const NAV_GLASS_TINT_ALPHA = 0.06;

/** Opacity of the glossy inner highlight on the active pill. */
export const NAV_ACTIVE_SHEEN_ALPHA = 0.35;

/** Opacity of the soft shadow that floats the bar off the content. */
export const NAV_BAR_SHADOW_ALPHA = 0.14;

/** Opacity of the indigo glow under the active pill. */
export const NAV_ACTIVE_GLOW_ALPHA = 0.5;

/** Opacity of the bar's hairline border. */
export const NAV_BORDER_ALPHA = 0.4;

/**
 * Maps a nav destination id to a clean CDS line-icon for the bottom nav + More sheet.
 * Falls back to the destination's own `navigationConfig` icon when an id is not listed.
 */
export const BOTTOM_NAV_ICONS = {
  home: HomeIcon,
  pipelines: TreeDiagramIcon,
  dashboards: GridSquaresIcon,
  reports: TrendUpIcon,
  documentData: DocumentIcon,
  pricingWorkbench: DollarIcon,
  'company-center': BuildingIcon,
  'company-manager': BankIcon,
  'applications-tab': FileSettingsIcon,
  'accounting-tab': WalletIcon,
  'system-tab': ApiIcon,
  [SEARCH_ITEM_ID]: SearchIcon,
  [PROFILE_ITEM_ID]: UserIcon,
  [NOTIFICATIONS_ITEM_ID]: AlertIcon,
  [MESSAGES_ITEM_ID]: SecureMessageIcon,
  [PHONE_ITEM_ID]: PhoneIcon,
  [CREATE_ITEM_ID]: PlusIcon,
};
